import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_URL_TOKEN, UserDevice } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

/**
 * Service for managing user devices associated with a tenant.
 * Provides capabilities to retrieve, search, and manage user devices.
 */
@Injectable({
    providedIn: 'root',
})
export class UserDeviceService {
    protected http = inject(HttpClient);
    protected baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Get all user devices for a tenant with optional pagination
     * @param tenantId The tenant ID
     * @param page Page number (default: 1)
     * @param pageSize Number of items per page (default: 25)
     */
    getAll(tenantId: string, page = 1, pageSize = 25): Observable<UserDevice[]> {
        const params = new HttpParams().set('page', page.toString()).set('page_size', pageSize.toString());

        return this.http.get<UserDevice[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice`, {
            params,
            headers: LoadingHeaders.httpHeadersWithNoLoading(),
        });
    }

    /**
     * Get all user devices for a specific user
     * @param tenantId The tenant ID
     * @param userId The user ID
     * @param page Page number (default: 1)
     * @param pageSize Number of items per page (default: 25)
     */
    getByUserId(tenantId: string, userId: string, page = 1, pageSize = 25): Observable<UserDevice[]> {
        const params = new HttpParams().set('page', page.toString()).set('page_size', pageSize.toString());

        return this.http.get<UserDevice[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/user/${userId}`, {
            params,
            headers: LoadingHeaders.httpHeadersWithNoLoading(),
        });
    }

    /**
     * Get a specific user device by ID
     * @param tenantId The tenant ID
     * @param id The user device ID
     */
    getById(tenantId: string, id: string): Observable<UserDevice> {
        return this.http.get<UserDevice>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/id/${id}`);
    }

    /**
     * Search for user devices with optional filters
     * @param tenantId The tenant ID
     * @param userId Optional user ID filter
     * @param keyword Optional keyword filter (searches device model and display name)
     * @param page Page number (default: 1)
     * @param pageSize Number of items per page (default: 25)
     */
    search(tenantId: string, userId?: string, keyword?: string, page = 1, pageSize = 25): Observable<UserDevice[]> {
        let params = new HttpParams().set('page', page.toString()).set('page-size', pageSize.toString());

        if (userId) {
            params = params.set('user-id', userId);
        }

        if (keyword) {
            params = params.set('keyword', keyword);
        }

        return this.http.get<UserDevice[]>(`${this.baseUrl}v1/tenant/${tenantId}/UserDevice/search`, {
            params,
            headers: LoadingHeaders.httpHeadersWithNoLoading(),
        });
    }
}
